import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Check, ArrowRight, CalendarPlus, X } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import ScorecardStrip from "../components/mobile/ScorecardStrip";
import { ListRow, RowAction, Section, EmptyRow } from "../components/mobile/ListRow";
import { fmtTime } from "../components/agenda/agendaUtils";
import { OnboardingChecklist } from "../components/OnboardingGate";
import { useT, locale } from "../lib/i18n";

const signalTone = (s) => (s.score >= 0.8 ? "red" : s.score >= 0.6 ? "orange" : "slate");

export default function TodayPage() {
  const t = useT();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/today"); setData(data); }
    catch { setError(t("Kon vandaag niet laden.")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const feedback = async (sig, action) => {
    setData((d) => ({ ...d, signals: d.signals.filter((s) => s.id !== sig.id) }));
    await api.post(`/assistant/signals/${sig.id}/feedback`, { action }).catch(() => {});
  };
  const decide = async (p, kind) => {
    setData((d) => ({ ...d, proposals: d.proposals.filter((x) => x.id !== p.id) }));
    if (kind === "accept") await api.post(`/agenda/proposals/${p.id}/accept`, {}).catch(() => {});
    else await api.post(`/agenda/proposals/${p.id}/reject`).catch(() => {});
    load();
  };

  const dateLabel = new Date().toLocaleDateString(locale(), { weekday: "long", day: "numeric", month: "long" });

  return (
    <Layout title={t("Vandaag")} subtitle={dateLabel} compact mobileFlush>
      <div className="max-w-xl lg:max-w-3xl" data-testid="today-page">
        {error && <div className="px-4 py-3 text-sm text-red-600" data-testid="today-error">{error}</div>}
        {!data && !error && <div className="px-4 py-6 text-sm text-slate-400">{t("Laden…")}</div>}
        {data && (
          <>
            <div className="pt-4 px-4 lg:px-0"><OnboardingChecklist /></div>
            <div><ScorecardStrip scorecard={data.scorecard} /></div>

            <Section title="Urgent" count={data.signals.length} to="/assistant" testid="today-urgent">
              {data.signals.length === 0 && <EmptyRow testid="today-urgent-empty">{t("Niets urgents — alles onder controle.")}</EmptyRow>}
              {data.signals.map((s) => (
                <ListRow key={s.id} tone={signalTone(s)} title={s.title} meta={s.detail} testid={`today-signal-${s.id}`}
                  onClick={() => { feedback(s, "acted"); nav(s.action_route); }}
                  actions={<>
                    {s.phone && <RowAction href={`tel:${s.phone}`} testid={`today-signal-call-${s.id}`} title={t("Bellen")}><Phone size={14} /></RowAction>}
                    <RowAction primary onClick={() => { feedback(s, "acted"); nav(s.action_route); }} testid={`today-signal-go-${s.id}`} title={s.action_label}><ArrowRight size={14} /></RowAction>
                    <RowAction onClick={() => feedback(s, "acted")} testid={`today-signal-done-${s.id}`} title={t("Afhandelen")}><Check size={14} /></RowAction>
                  </>}
                />
              ))}
            </Section>

            <Section title="Agenda" count={data.agenda.length} to="/agenda" testid="today-agenda">
              {data.agenda.length === 0 && <EmptyRow testid="today-agenda-empty">{t("Geen afspraken vandaag.")}</EmptyRow>}
              {data.agenda.map((e) => (
                <ListRow key={e.id} tone={e.past ? "slate" : "green"} leading={fmtTime(e.start)} title={e.title}
                  meta={[e.client_name, e.contact_name && `${e.contact_name}${e.contact_org ? ` · ${e.contact_org}` : ""}`, e.location].filter(Boolean).join(" · ") || null}
                  testid={`today-event-${e.id}`} onClick={() => nav("/agenda")}
                  actions={e.phone ? <RowAction href={`tel:${e.phone}`} testid={`today-event-call-${e.id}`} title={t("Bellen")}><Phone size={14} /></RowAction> : null}
                />
              ))}
            </Section>

            {data.proposals.length > 0 && (
              <Section title={t("AI Suggesties")} count={data.proposals.length} to="/agenda?tab=proposals" testid="today-proposals">
                {data.proposals.map((p) => (
                  <ListRow key={p.id} tone="orange" title={p.title}
                    meta={`${p.sender_name || p.sender_email}${p.start ? ` · ${new Date(p.start).toLocaleDateString(locale(), { weekday: "short", day: "numeric", month: "short" })} ${fmtTime(p.start)}` : ` · ${t("geen concrete tijd")}`}`}
                    testid={`today-proposal-${p.id}`} onClick={() => nav("/agenda?tab=proposals")}
                    actions={<>
                      <RowAction primary onClick={() => (p.start ? decide(p, "accept") : nav("/agenda?tab=proposals"))} testid={`today-proposal-accept-${p.id}`} title={t("Inplannen")}><CalendarPlus size={14} /></RowAction>
                      <RowAction onClick={() => decide(p, "reject")} testid={`today-proposal-reject-${p.id}`} title={t("Afwijzen")}><X size={14} /></RowAction>
                    </>}
                  />
                ))}
              </Section>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
